import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DevicesFilterOption } from '../models/device-filter-option';
import { DeviceRegisterOptions } from '../models/device-register-options';
import { getDevicesQuery } from '../queries/devices.graphql';
import { GetDevices, GetDevicesVariables } from '../queries/generated/GetDevices';
import { SaveSipUsers, SaveSipUsersVariables } from '../queries/generated/SaveSipUsers';
import { SaveSipUserMutation } from '../queries/save-sipuser.graphql';

@Injectable({ providedIn: 'root' })
export class DeviceRegisterApiService {
   constructor(private apiService: LcsApiService, private httpClient: HttpClient) {}

   getDevices(
      endCursor: string | null,
      filters: DevicesFilterOption[] | null,
      quickFilters: DeviceRegisterOptions | null,
      orderingOptions: OrderingOption[] | null,
      first: number | null
   ): Observable<ExecutionResult<GetDevices>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertDeviceFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'MacAddress',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Account.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'DeviceType.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Phone.ExtensionPhone.DefaultPhoneNumber.PhoneNumber',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Phone.SipUsers.UserName',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'BaseStationHandsets.Phone.HandsetIpei',
               operation: FilterOperations.Contains,
               value: searchText,
            });

            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
         if (quickFilters.typeName) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'DeviceType.DeviceTypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.typeName.deviceTypeId,
            });
         }
         if (quickFilters.hideUsedDevices) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'PhoneId',
               operation: FilterOperations.HasValue,
               value: false,
            });
         }
         if (quickFilters.showDevicesWithIssues) {
            const subExpression: FilterExpression = { filterOptions: [] };
            subExpression.filterOptions.push({
               field: 'Phone.SipUsers.IsHacked',
               operation: FilterOperations.EqualTo,
               value: true,
            });
            subExpression.logicalOperator = LogicalOperators.Or;
            subExpression.filterOptions.push({
               field: 'Phone.SipUsers.IsFraud',
               operation: FilterOperations.EqualTo,
               value: true,
            });

            filterBuilderFilterExpression.subExpressions = filterBuilderFilterExpression.subExpressions
               ? [...filterBuilderFilterExpression.subExpressions, subExpression]
               : [subExpression];
         }
      }
      const variables = this.getDevicesVariables(endCursor, filterBuilderFilterExpression, orderingOptions, first);

      return this.apiService.query<GetDevices, GetDevicesVariables>({
         query: getDevicesQuery,
         variables,
      });
   }

   changeSipUserPasswords(sipUserIds: number[]): Observable<ExecutionResult<SaveSipUsers>> {
      const sipUsers = sipUserIds.map((sipUserId) => ({
         sipUserId: sipUserId,
         resetSipUserPassword: true,
         fields: ['resetSipUserPassword'],
      }));

      return this.apiService.query<SaveSipUsers, SaveSipUsersVariables>({
         query: SaveSipUserMutation,
         variables: {
            sipUsers: sipUsers,
         },
      });
   }

   rebootPhone(deviceId: number): Observable<any> {
      return this.httpClient.post(this.apiService.apiUrl + '/Devices/Reboot', { deviceId });
   }

   resetPhone(deviceId: number): Observable<any> {
      return this.httpClient.post(this.apiService.apiUrl + '/Devices/FactoryReset', { deviceId });
   }

   private convertDeviceFilterOption(option: DevicesFilterOption): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: DevicesFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'device':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.device;
                 })
               : option.value?.device || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getDevicesVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null,
      first: number | null
   ): GetDevicesVariables {
      const devicesToRetrieve = first ?? 50;
      const variables: GetDevicesVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: devicesToRetrieve,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
